import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const API_BASE = `${API_BASE_URL}/api/node-network`;

export const nodeNetworkService = {
  // ── Networks ──────────────────────────────────────────────
  getNetworks: async () => {
    const response = await axios.get(API_BASE);
    return response.data;
  },

  createNetwork: async (data) => {
    const response = await axios.post(API_BASE, data);
    return response.data;
  },

  getNetwork: async (networkId) => {
    const response = await axios.get(`${API_BASE}/${networkId}`);
    return response.data;
  },

  updateNetwork: async (networkId, data) => {
    const response = await axios.put(`${API_BASE}/${networkId}`, data);
    return response.data;
  },

  deleteNetwork: async (networkId) => {
    const response = await axios.delete(`${API_BASE}/${networkId}`);
    return response.data;
  },

  // ── Graph ─────────────────────────────────────────────────
  getNetworkGraph: async (networkId) => {
    const response = await axios.get(`${API_BASE}/${networkId}/graph`);
    return response.data;
  },

  // Resolve the LEI of a node's linked OFP organization (Phase 1, GLEIF/vLEI)
  getNodeOfpLei: async (nodeId) => {
    const response = await axios.get(`${API_BASE}/nodes/${encodeURIComponent(nodeId)}/ofp-lei`);
    return response.data; // { ofp_organization_id, lei, urn }
  },

  // ── Nodes ─────────────────────────────────────────────────
  getNodes: async (networkId) => {
    const response = await axios.get(`${API_BASE}/${networkId}/nodes`);
    return response.data;
  },

  createNode: async (networkId, data) => {
    const response = await axios.post(`${API_BASE}/${networkId}/nodes`, data);
    return response.data;
  },

  getNode: async (nodeId) => {
    const response = await axios.get(`${API_BASE}/nodes/${nodeId}`);
    return response.data;
  },

  updateNode: async (nodeId, data) => {
    const response = await axios.put(`${API_BASE}/nodes/${nodeId}`, data);
    return response.data;
  },

  deleteNode: async (nodeId) => {
    const response = await axios.delete(`${API_BASE}/nodes/${nodeId}`);
    return response.data;
  },

  // ── Legal Identities ──────────────────────────────────────
  getLegalIdentities: async (nodeId) => {
    const response = await axios.get(`${API_BASE}/nodes/${nodeId}/legal-identities`);
    return response.data;
  },

  createLegalIdentity: async (nodeId, data) => {
    const response = await axios.post(`${API_BASE}/nodes/${nodeId}/legal-identities`, data);
    return response.data;
  },

  updateLegalIdentity: async (nodeId, lid, data) => {
    const response = await axios.put(`${API_BASE}/nodes/${nodeId}/legal-identities/${lid}`, data);
    return response.data;
  },

  deleteLegalIdentity: async (nodeId, lid) => {
    const response = await axios.delete(`${API_BASE}/nodes/${nodeId}/legal-identities/${lid}`);
    return response.data;
  },

  // ── Connections ───────────────────────────────────────────
  getConnections: async (nodeId) => {
    const response = await axios.get(`${API_BASE}/nodes/${nodeId}/connections`);
    return response.data;
  },

  createConnection: async (nodeId, data) => {
    const response = await axios.post(`${API_BASE}/nodes/${nodeId}/connections`, data);
    return response.data;
  },

  deleteConnection: async (connectionId) => {
    const response = await axios.delete(`${API_BASE}/connections/${connectionId}`);
    return response.data;
  },

  // ── dVE Import ────────────────────────────────────────────
  importDveCorridorNetwork: async () => {
    const response = await axios.post(`${API_BASE}/import/dve-corridor`);
    return response.data;
  },

  // ── Dashboard ────────────────────────────────────────────
  getDashboard: async () => {
    const response = await axios.get(`${API_BASE}/dashboard`);
    return response.data;
  },

  // ── PACT Directory Sync ──────────────────────────────────
  pactHealthCheck: async () => {
    const response = await axios.get(`${API_BASE}/pact/health`);
    return response.data;
  },

  pushNodeToPACT: async (nodeId) => {
    const response = await axios.post(`${API_BASE}/nodes/${nodeId}/pact/push`);
    return response.data;
  },

  syncNodeFromPACT: async (nodeId, apply = false) => {
    const response = await axios.get(`${API_BASE}/nodes/${nodeId}/pact/sync`, { params: { apply } });
    return response.data;
  },

  pushConnectionToPACT: async (connectionId) => {
    const response = await axios.post(`${API_BASE}/connections/${connectionId}/pact/push`);
    return response.data;
  },

  acceptPACTConnection: async (connectionId) => {
    const response = await axios.post(`${API_BASE}/connections/${connectionId}/pact/accept`);
    return response.data;
  },

  rotatePACTCredentials: async (connectionId) => {
    const response = await axios.post(`${API_BASE}/connections/${connectionId}/pact/rotate-credentials`);
    return response.data;
  },

  pushNetworkToPACT: async (networkId) => {
    const response = await axios.post(`${API_BASE}/${networkId}/pact/push-all`);
    return response.data;
  }
};
